import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import Button from "../Button";
import { Typography } from "../Typography";
import { sendBulkEmail } from "../../Utils/apiServices";

const SendBulkEmailModal = ({ isOpen, onClose, emails = [] }) => {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setError("");
      setSuccess(false);
    }
  }, [isOpen]);

  const handleSend = async () => {
    if (emails.length === 0) {
      setError("No emails to send to");
      return;
    }

    try {
      setSending(true);
      setError("");
      setSuccess(false);

      const response = await sendBulkEmail({
        email_ids: emails,
        template_code: "v_12",
      });

      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
          setSuccess(false);
        }, 2000);
      } else {
        setError(response.error || "Failed to send bulk email");
      }
    } catch (error) {
      console.error("Error sending bulk email:", error);
      setError(error.error || "An error occurred while sending emails");
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;


  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-slate-200 bg-gradient-to-r from-purple-50 to-blue-50">
          <div className="flex items-center gap-4 flex-1">
            <div className="h-12 w-12 inline-flex items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 shadow-lg">
              <Icon icon="lucide:mail" className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <Typography as="h2" className="text-2xl font-bold text-primary-900 text-left">
                Send Bulk Email
              </Typography>
              <Typography as="p" className="text-sm text-tertiary-600 mt-1 text-left">
                Send email to registered Volunteers.
              </Typography>
            </div>
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <Icon icon="lucide:x" className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
          <div className="flex flex-col gap-6">
            {/* Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Icon icon="lucide:file-text" className="h-5 w-5 text-purple-600" />
                  </div>
                  <Typography as="p" className="text-xs font-medium text-tertiary-600 uppercase tracking-wide">
                    Template Code
                  </Typography>
                </div>
                <Typography as="p" className="text-lg font-semibold text-primary-900">
                  v_12
                </Typography>
              </div>
              <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Icon icon="lucide:users" className="h-5 w-5 text-blue-600" />
                  </div>
                  <Typography as="p" className="text-xs font-medium text-tertiary-600 uppercase tracking-wide">
                    Total Recipients
                  </Typography>
                </div>
                <Typography as="p" className="text-lg font-semibold text-primary-900">
                  {emails.length}
                </Typography>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border-l-4 border-red-400 rounded-lg p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  <Icon
                    icon="lucide:alert-circle"
                    className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5"
                  />
                  <div className="flex-1">
                    <Typography as="p" className="text-sm font-medium text-red-800 mb-1">
                      Error sending emails
                    </Typography>
                    <Typography as="p" className="text-sm text-red-600">
                      {error}
                    </Typography>
                  </div>
                </div>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="bg-green-50 border-l-4 border-green-400 rounded-lg p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  <Icon
                    icon="lucide:check-circle-2"
                    className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5"
                  />
                  <div className="flex-1">
                    <Typography as="p" className="text-sm font-medium text-green-800 mb-1">
                      Emails sent successfully!
                    </Typography>
                    <Typography as="p" className="text-sm text-green-600">
                      All {emails.length} email{emails.length !== 1 ? "s" : ""} have been sent
                      successfully.
                    </Typography>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200 bg-white">
          <Button variant="secondary" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || emails.length === 0}
            startIcon={
              sending ? (
                <Icon icon="svg-spinners:ring-resize" className="h-4 w-4" />
              ) : (
                <Icon icon="lucide:send" className="h-4 w-4" />
              )
            }
          >
            {sending ? "Sending..." : "Send Email"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SendBulkEmailModal;

